import { Collapse, IconButton, Paper, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { memo, useState } from 'react';

import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import { MapType } from '../../../domain/entities/map.types';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SatelliteIcon from '@mui/icons-material/Satellite';
import TerrainIcon from '@mui/icons-material/Terrain';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import tid from '@/common/infrastructure/config/tid';

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  mapType: MapType;
  onMapTypeChange: (event: React.MouseEvent<HTMLElement>, newMapType: MapType | null) => void;
  onReset: () => void;
}

const MAP_TYPES: { value: MapType; icon: typeof MapIcon; label: string }[] = [
  { value: 'roadmap', icon: MapIcon, label: 'Road Map' },
  { value: 'satellite', icon: SatelliteIcon, label: 'Satellite' },
  { value: 'terrain', icon: TerrainIcon, label: 'Terrain' },
  { value: 'hybrid', icon: LayersIcon, label: 'Hybrid' }
];

const MapControlsComponent: React.FC<MapControlsProps> = ({ onZoomIn, onZoomOut, mapType, onMapTypeChange, onReset }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMapTypeSelect = (event: React.MouseEvent<HTMLElement>, newMapType: MapType | null) => {
    if (newMapType) {
      onMapTypeChange(event, newMapType);
      setIsExpanded(false);
    }
  };

  const currentMapType = MAP_TYPES.find((type) => type.value === mapType);
  const Icon = currentMapType?.icon || MapIcon;

  return (
    <div
      style={{
        position: 'absolute',
        right: 8,
        bottom: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        zIndex: 10
      }}
    >
      <Paper elevation={5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Collapse
          in={isExpanded}
          timeout={300}
          easing="ease-in-out"
          sx={{
            transformOrigin: 'bottom',
            '& .MuiCollapse-wrapper': {
              transformOrigin: 'bottom'
            }
          }}
        >
          <ToggleButtonGroup value={mapType} exclusive onChange={handleMapTypeSelect} size="small" orientation="vertical">
            {MAP_TYPES.map(({ value, icon: MapTypeIcon, label }) => (
              <Tooltip key={value} title={label} placement="left">
                <ToggleButton value={value} aria-label={label} sx={{ p: 0.5 }} {...tid(`map:controls:map-type-${value}-button`)}>
                  <MapTypeIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
        </Collapse>
        <Tooltip title={`${isExpanded ? 'Hide' : 'Show'} map type options`} placement="left">
          <ToggleButton
            value={mapType}
            onClick={handleToggleExpand}
            size="small"
            sx={{
              p: 0.5,
              transition: 'all 0.2s ease-in-out'
            }}
            aria-label={`Current: ${currentMapType?.label || 'unknown'}. Click to ${isExpanded ? 'hide' : 'show'} map type options`}
            {...tid('map:controls:map-type-toggle-button')}
          >
            <Icon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      </Paper>

      <Paper elevation={5}>
        <ToggleButtonGroup orientation="vertical" size="small">
          <Tooltip title="Zoom In" placement="left">
            <ToggleButton
              value="zoom-in"
              onClick={onZoomIn}
              size="small"
              sx={{ p: 0.5 }}
              aria-label="Zoom In"
              {...tid('map:controls:zoom-in-button')}
            >
              <ZoomInIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Zoom Out" placement="left">
            <ToggleButton
              value="zoom-out"
              onClick={onZoomOut}
              size="small"
              sx={{ p: 0.5 }}
              aria-label="Zoom Out"
              {...tid('map:controls:zoom-out-button')}
            >
              <ZoomOutIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Paper>
      <Paper elevation={5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Tooltip title="Reset View" placement="left">
          <IconButton
            onClick={onReset}
            size="small"
            sx={{
              p: 0.5,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
            aria-label="Reset View"
            {...tid('map:controls:reset-button')}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </div>
  );
};

const MapControls = memo(MapControlsComponent);

export default MapControls;
